"use client"

import { useState, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Info } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { useAppointmentModal } from "@/contexts/AppointmentModalContext"
import { Guard } from "@/components/access/Guard"
import { formatCurrency } from "@/lib/utils"
import { WriteToClientButton } from "@/components/inbox/InboxEntry"
import { ClientSummaryPanel } from "./client-summary-panel"
import { ClientCategoryPicker } from "./client-category-picker"

function useDebouncedHandler<T>(handler: (value: T) => void, delay = 500) {
  const timeout = useRef<NodeJS.Timeout | null>(null)
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  return (value: T) => {
    if (timeout.current) clearTimeout(timeout.current)
    timeout.current = setTimeout(() => {
      handlerRef.current(value)
    }, delay)
  }
}

export function ClientDataSection() {
  const { t } = useTranslation()
  const { appointment, updateAppointment } = useAppointmentModal()

  // Gender mapping constants
  const GENDER_MALE = t("appointment.client.gender_male", "Male")
  const GENDER_FEMALE = t("appointment.client.gender_female", "Female")
  const GENDER_NOT_SELECTED = t("appointment.client.gender_not_selected", "Not selected")

  // Local state for debounced text fields
  const [name, setName] = useState(appointment?.client?.name || "")
  const [phone, setPhone] = useState(appointment?.client?.phone || "")
  const [email, setEmail] = useState(appointment?.client?.email || "")
  const [birthDate, setBirthDate] = useState(appointment?.client?.birthday || "")
  const [cardNumber, setCardNumber] = useState(appointment?.client?.card_number || "")
  const [discount, setDiscount] = useState(
    typeof appointment?.client?.discount === "number" ? String(appointment.client.discount) : "0"
  )
  const [notes, setNotes] = useState(appointment?.client?.comment || "")
  const [gender, setGender] = useState(
    typeof appointment?.client?.gender === "number"
      ? appointment.client.gender === 1
        ? GENDER_MALE
        : appointment.client.gender === 2
          ? GENDER_FEMALE
          : GENDER_NOT_SELECTED
      : GENDER_NOT_SELECTED
  )
  const [disableOnlineBooking, setDisableOnlineBooking] = useState(!!appointment?.disableOnlineBooking)

  // Sync local state with appointment data when it changes
  useEffect(() => {
    setName(appointment?.client?.name || "")
    setPhone(appointment?.client?.phone || "")
    setEmail(appointment?.client?.email || "")
    setBirthDate(appointment?.client?.birthday || "")
    setCardNumber(appointment?.client?.card_number || "")
    setDiscount(
      typeof appointment?.client?.discount === "number" ? String(appointment.client.discount) : "0"
    )
    setNotes(appointment?.client?.comment || "")
    setGender(
      typeof appointment?.client?.gender === "number"
        ? appointment.client.gender === 1
          ? GENDER_MALE
          : appointment.client.gender === 2
            ? GENDER_FEMALE
            : GENDER_NOT_SELECTED
        : GENDER_NOT_SELECTED
    )
    setDisableOnlineBooking(!!appointment?.disableOnlineBooking)
  }, [appointment, GENDER_MALE, GENDER_FEMALE, GENDER_NOT_SELECTED])

  // Handler for name
  const handleNameChange = useDebouncedHandler((v: string) => {
    updateAppointment({
      client: { name: v } as any
    })
  }, 500)

  // Handler for phone
  const handlePhoneChange = useDebouncedHandler((v: string) => {
    updateAppointment({
      client: { phone: v } as any
    })
  }, 500)

  // Handler for email
  const handleEmailChange = useDebouncedHandler((v: string) => {
    updateAppointment({
      client: { email: v } as any
    })
  }, 500)

  // Handler for birth date
  const handleBirthDateChange = useDebouncedHandler((v: string) => {
    updateAppointment({
      client: { birthday: v } as any
    })
  }, 500)

  // Handler for card number
  const handleCardNumberChange = useDebouncedHandler((v: string) => {
    updateAppointment({
      client: { card_number: v } as any
    })
  }, 500)

  // Handler for discount
  const handleDiscountChange = useDebouncedHandler((v: string) => {
    updateAppointment({
      client: { discount: Number(v) } as any
    })
  }, 500)

  // Handler for notes
  const handleNotesChange = useDebouncedHandler((v: string) => {
    updateAppointment({
      client: { comment: v } as any
    })
  }, 500)

  // Handler for gender
  const handleGenderChange = (v: string) => {
    let genderValue = 0
    if (v === GENDER_MALE) genderValue = 1
    else if (v === GENDER_FEMALE) genderValue = 2
    updateAppointment({
      client: { gender: genderValue } as any
    })
    setGender(v)
  }

  // Handler for disableOnlineBooking
  const handleDisableOnlineBookingChange = (checked: boolean) => {
    updateAppointment({
      disableOnlineBooking: checked,
      // persisted on the client card as is_blocked; the backend routes both keys
      client: { ...(appointment?.client ?? {}), is_blocked: checked },
    } as Parameters<typeof updateAppointment>[0])
    setDisableOnlineBooking(checked)
  }

  const stat = appointment?.clientRecordStatistic
  const salesAmount = stat && typeof stat.total_revenue === "number"
    ? formatCurrency(stat.total_revenue)
    : "—"
  const paidAmount = salesAmount
  const balance = appointment?.balance || "0"
  const visitsCount = stat?.visits_count ?? "—"
  const tags = appointment?.detailsSection?.clientTags || []

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
        {/* Left column - Client form */}
        <div className="col-span-1 lg:col-span-2 space-y-5">
          {/* Boona Direct (renders nothing when the module is off) */}
          <WriteToClientButton clientId={Number(appointment?.client?.id) || null} />
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t("appointment.client.name", "Name")}</label>
            <Input
              value={name}
              onChange={e => {
                setName(e.target.value)
                handleNameChange(e.target.value)
              }}
              className="border-gray-300 h-9 touch-manipulation select-text"
            />
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("appointment.client.phone", "Phone")}</label>
              <Guard
                check={(p) => p.timetable.phonesVisible}
                mode="render"
                fallback={<div className="text-xs text-gray-500">{t("appointment.client.hidden_by_access", "Hidden by access settings")}</div>}
              >
                <Input
                  value={phone}
                  onChange={e => {
                    setPhone(e.target.value)
                    handlePhoneChange(e.target.value)
                  }}
                  className="border-gray-300 h-9 touch-manipulation select-text"
                />
              </Guard>
            </div>
            {/* "Additional phone" removed 2026-10-07: the client card has no column for it, so whatever was typed was dropped on save (clients README §5.2 #15) */}
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Email</label>
            <Input
              value={email}
              onChange={e => {
                setEmail(e.target.value)
                handleEmailChange(e.target.value)
              }}
              className="border-gray-300 h-9 touch-manipulation select-text"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("appointment.client.birth_date", "Birth date")}</label>
              <Input
                value={birthDate}
                onChange={e => {
                  setBirthDate(e.target.value)
                  handleBirthDateChange(e.target.value)
                }}
                className="border-gray-300 h-9 touch-manipulation select-text"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("appointment.client.gender", "Gender")}</label>
              <div className="relative">
                <select
                  className="w-full h-9 px-3 border border-gray-300 rounded-md appearance-none text-sm"
                  value={gender}
                  onChange={e => handleGenderChange(e.target.value)}
                >
                  <option>{GENDER_NOT_SELECTED}</option>
                  <option>{GENDER_MALE}</option>
                  <option>{GENDER_FEMALE}</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div>
            {/* the salon's categories (Client settings), ticked here and saved with the visit
                like the fields above, so the visit's history shows the change; the YClients
                "importance class" that sat below was never set by any salon - removed */}
            <label className="block text-xs text-gray-500 mb-1">{t("appointment.client.category", "Category")}</label>
            <ClientCategoryPicker
              key={Number(appointment?.client?.id) || 0}
              clientId={Number(appointment?.client?.id) || null}
              initial={tags}
              value={(appointment?.client as any)?.category_ids}
              onChange={(ids) => updateAppointment({ client: { category_ids: ids } as any })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("appointment.client.card_number", "Card number")}</label>
              <Input
                value={cardNumber}
                onChange={e => {
                  setCardNumber(e.target.value)
                  handleCardNumberChange(e.target.value)
                }}
                className="border-gray-300 h-9"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("appointment.client.discount_percent", "Discount, %")}</label>
              <Input
                value={discount}
                onChange={e => {
                  setDiscount(e.target.value)
                  handleDiscountChange(e.target.value)
                }}
                className="border-gray-300 h-9"
              />
            </div>
          </div>

          <div>
            {/* yclient.comment: the card's pinned note, shown on every visit (clients D10) */}
            <label className="block text-xs text-gray-500 mb-1">{t("appointment.client.pinned_note", "Pinned note — shown on every visit")}</label>
            <Textarea
              value={notes}
              onChange={e => {
                setNotes(e.target.value)
                handleNotesChange(e.target.value)
              }}
              className="border-gray-300 min-h-[100px] text-base md:text-sm touch-manipulation"
            />
          </div>

          <div className="flex items-center">
            <Checkbox
              id="disable-online-booking"
              checked={disableOnlineBooking}
              onCheckedChange={handleDisableOnlineBookingChange}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="disable-online-booking" className="ml-2 text-sm">
              {t("appointment.client.disable_online_booking", "Disable online booking")}
            </label>
          </div>

          <div className="pt-4 border-t">
            <div className="grid grid-cols-3 gap-4 mb-2">
              <div>
                <div className="text-xs text-gray-500 mb-1">{t("appointment.client.sold_services_products", "Services & products sold, Br")}</div>
                <div className="text-sm font-medium">{salesAmount}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">{t("appointment.client.paid_to_register", "Paid to register, Br")}</div>
                <div className="text-sm font-medium">{paidAmount}</div>
              </div>
              <div>
                <div className="flex items-center text-xs text-gray-500 mb-1">
                  <span>{t("appointment.client.balance", "Balance, Br")}</span>
                  <Info className="w-3.5 h-3.5 ml-1 text-gray-400" />
                </div>
                <div className="text-sm font-medium">{balance}</div>
              </div>
            </div>
            <div className="text-xs text-gray-500 mb-3">{t("appointment.client.for_visits", "For {{count}} visits", { count: visitsCount })}</div>
            <Button variant="outline" size="sm" className="text-xs h-8" disabled>
              {t("appointment.client.view_visit_history", "View visit history")}
            </Button>
          </div>
        </div>

        {/* Right column - the client card at a glance (boona clients CL3b). The photo upload
            and "New comment" here were placeholders that saved nothing; notes live on the card. */}
        <div className="col-span-1 lg:col-span-1 space-y-5">
          <ClientSummaryPanel clientId={Number(appointment?.client?.id) || null} recordId={Number(appointment?.id) || null} />
        </div>
      </div>
    </div>
  )
}
